import {useState} from 'react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import type {QueryKey} from '@tanstack/react-query'
import {Cable, Copy, Link2Off, Loader2, MoreHorizontal} from 'lucide-react'
import {api} from '@/api/client'
import {iamBase} from '@/lib/runtime-config'
import {useServices} from '@/services/context'
import {useI18n} from '@/i18n'
import {useToast} from '@/hooks/use-toast'
import {formatDateOnly} from '@/lib/date-utils'
import {CardSection} from '@/components/shared/CardSection'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {TargetSystemLink} from '@/components/shared/TargetSystemLink'
import {Skeleton} from '@/components/ui/skeleton'
import {Button} from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import type {DetachSourceResponse, Source} from '@/types/source'

/**
 * Entity type slug used by the {@code /sources} REST endpoints. Mirrors the
 * route segment Spring registers (Spec §5):
 * {@code /api/i/{entityType}/{id}/sources}.
 */
/* eslint-disable i18next/no-literal-string -- enum-style constant for API path slugs */
export const SourcesEntityType = {
    identities: 'identities',
    parties: 'parties',
} as const
/* eslint-enable i18next/no-literal-string */
export type SourcesEntityType = typeof SourcesEntityType[keyof typeof SourcesEntityType]

export interface SourcesCardProps {
    entityType: SourcesEntityType
    entityId: string
    /** When false, hides the per-row action menu (Detach / Bulk release). */
    canEdit: boolean
    /**
     * Cache keys to invalidate after a successful Detach / Bulk release. The
     * caller passes the entity-detail key + the attributes key so the
     * AttributeAuthorityBadge flips back to "Unclaimed" on affected rows.
     */
    invalidateOnMutate?: ReadonlyArray<QueryKey>
}

/* eslint-disable i18next/no-literal-string -- API paths, CSS class names, testid suffixes */

const SOURCES_QUERY_KEY = 'sources' as const
const ATTRIBUTE_LIST_QUERY_KEY = 'attributes' as const
const AUTHORITY_OWNED_COUNT_QUERY_KEY = 'authority-owned-count' as const

/**
 * Sources card (Spec §6.1) — shows which external systems sync into this
 * record. Renders four states:
 *
 *   - Loading — single skeleton row, no action menu
 *   - Empty — icon + "No external sources" + manual-creation subtext
 *   - Error — inline message + Retry button (page does not crash)
 *   - Populated — one row per source with system name, truncated
 *     external ID + click-to-copy, last-seen relative badge, and
 *     (admin-only) per-row kebab with Detach + Release-all actions
 */
export function SourcesCard({
    entityType,
    entityId,
    canEdit,
    invalidateOnMutate,
}: SourcesCardProps) {
    const i18n = useI18n()
    const services = useServices()
    const queryKey: QueryKey = [entityType, entityId, SOURCES_QUERY_KEY]
    const sourcesService = entityType === SourcesEntityType.identities
        ? services.identities
        : services.parties

    const {data, isLoading, isError, refetch, isFetching} = useQuery({
        queryKey,
        queryFn: () => sourcesService.sources(entityId),
        enabled: !!entityId,
    })

    const sources = data?.items ?? []

    return (
        <CardSection
            icon={Cable}
            title={i18n.sources.title as string}
            data-testid="section-sources"
            data-help-prefix="sourcesCard"
        >
            {isLoading ? (
                <SourcesLoading/>
            ) : isError ? (
                <SourcesErrorState
                    onRetry={() => refetch()}
                    retrying={isFetching}
                />
            ) : sources.length === 0 ? (
                <SourcesEmpty/>
            ) : (
                <Stack gap="sm" divideY>
                    {sources.map((source) => (
                        <SourceRow
                            key={source.id}
                            entityType={entityType}
                            entityId={entityId}
                            source={source}
                            canEdit={canEdit}
                            sourcesQueryKey={queryKey}
                            invalidateOnMutate={invalidateOnMutate}
                        />
                    ))}
                </Stack>
            )}
        </CardSection>
    )
}

function SourcesLoading() {
    return (
        <div className="py-2" data-testid="sources-loading">
            <Skeleton className="h-12 w-full"/>
        </div>
    )
}

function SourcesEmpty() {
    const i18n = useI18n()
    return (
        <Stack gap="xs" align="center" className="py-6 text-center" data-testid="sources-empty">
            <Link2Off className="h-8 w-8 text-muted-foreground" aria-hidden="true"/>
            <Strong>{i18n.sources.empty}</Strong>
            <MutedText size="sm" centered>{i18n.sources.emptySubtext}</MutedText>
        </Stack>
    )
}

interface SourcesErrorStateProps {
    onRetry: () => void
    retrying: boolean
}

function SourcesErrorState({onRetry, retrying}: SourcesErrorStateProps) {
    const i18n = useI18n()
    return (
        <Stack gap="sm" align="center" className="py-6 text-center" data-testid="sources-error">
            <MutedText size="sm" centered>{i18n.sources.loadError}</MutedText>
            <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                disabled={retrying}
                data-testid="sources-retry"
            >
                {i18n.common.retry}
            </Button>
        </Stack>
    )
}

interface SourceRowProps {
    entityType: SourcesEntityType
    entityId: string
    source: Source
    canEdit: boolean
    sourcesQueryKey: QueryKey
    invalidateOnMutate?: ReadonlyArray<QueryKey>
}

function SourceRow({
    entityType,
    entityId,
    source,
    canEdit,
    sourcesQueryKey,
    invalidateOnMutate,
}: SourceRowProps) {
    const i18n = useI18n()
    const {toast} = useToast()
    const truncated = truncate(source.externalId, 24)
    const firstSeenDate = formatDateOnly(source.firstSeenAt)

    const handleCopy = () => {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            void navigator.clipboard.writeText(source.externalId).then(() => {
                toast({description: i18n.sources.externalIdCopied})
            }).catch(() => {
                toast({description: i18n.sources.externalIdCopyFailed, variant: 'destructive'})
            })
        }
    }

    return (
        <InlineRow
            gap="xl"
            align="center"
            className="py-3"
            data-testid={`source-row-${source.id}`}
        >
            <Cable className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true"/>
            <div className="flex-1 min-w-0">
                <InlineRow gap="xs" align="center">
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    className="font-mono text-xs text-muted-foreground hover:text-foreground hover:underline truncate text-left max-w-[20rem]"
                                    data-testid={`source-external-id-${source.id}`}
                                    aria-label={i18n.sources.copyExternalId}
                                >
                                    {truncated}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <span className="font-mono text-xs">{source.externalId}</span>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <Copy className="h-3 w-3 text-muted-foreground" aria-hidden="true"/>
                </InlineRow>
                <MutedText size="xs" className="mt-0.5">
                    {i18n.sources.firstSeen({date: firstSeenDate})}
                </MutedText>
            </div>
            <div className="w-44 shrink-0 flex items-center gap-1 justify-end">
                <TargetSystemLink
                    targetSystemId={source.targetSystemId}
                    targetSystemName={source.targetSystemName}
                    data-testid={`source-target-system-${source.id}`}
                />
                {canEdit && (
                    <SourceRowMenu
                        entityType={entityType}
                        entityId={entityId}
                        source={source}
                        sourcesQueryKey={sourcesQueryKey}
                        invalidateOnMutate={invalidateOnMutate}
                    />
                )}
            </div>
            <div className="w-[4.5rem] shrink-0" aria-hidden="true"/>
        </InlineRow>
    )
}

interface SourceRowMenuProps {
    entityType: SourcesEntityType
    entityId: string
    source: Source
    sourcesQueryKey: QueryKey
    invalidateOnMutate?: ReadonlyArray<QueryKey>
}

function SourceRowMenu({
    entityType,
    entityId,
    source,
    sourcesQueryKey,
    invalidateOnMutate,
}: SourceRowMenuProps) {
    const i18n = useI18n()
    const [menuOpen, setMenuOpen] = useState(false)
    const [detachOpen, setDetachOpen] = useState(false)
    const [bulkOpen, setBulkOpen] = useState(false)

    const ownedCountUrl = iamBase(
        `/${entityType}/${entityId}/attributes/owned-by-system-count?targetSystemId=${source.targetSystemId}`,
    )
    const ownedCountQuery = useQuery({
        queryKey: [entityType, entityId, AUTHORITY_OWNED_COUNT_QUERY_KEY, source.targetSystemId],
        queryFn: () => api.get<{count: number}>(ownedCountUrl),
        enabled: menuOpen,
        staleTime: 0,
    })

    const ownedCount = ownedCountQuery.data?.count ?? 0
    const countLoading = menuOpen && ownedCountQuery.isLoading
    const countErrored = menuOpen && ownedCountQuery.isError
    const releaseAllDisabled = countLoading || countErrored || ownedCount === 0

    return (
        <>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={i18n.sources.actionsMenu}
                        data-testid={`source-row-menu-${source.id}`}
                    >
                        <MoreHorizontal className="h-4 w-4"/>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        data-testid={`source-row-detach-${source.id}`}
                        onSelect={(e) => {
                            e.preventDefault()
                            setDetachOpen(true)
                        }}
                    >
                        {i18n.sources.detach}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        data-testid={`source-row-release-all-${source.id}`}
                        disabled={releaseAllDisabled}
                        onSelect={(e) => {
                            e.preventDefault()
                            if (!releaseAllDisabled) {
                                setBulkOpen(true)
                            }
                        }}
                    >
                        <span className="flex items-center gap-2">
                            {countLoading && <Loader2 className="h-3 w-3 animate-spin"/>}
                            {i18n.sources.releaseAll}
                            {!countLoading && !countErrored && (
                                <span className="text-xs text-muted-foreground">({ownedCount})</span>
                            )}
                        </span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            {detachOpen && (
                <DetachSourceDialog
                    open={detachOpen}
                    onOpenChange={setDetachOpen}
                    entityType={entityType}
                    entityId={entityId}
                    source={source}
                    sourcesQueryKey={sourcesQueryKey}
                    invalidateOnMutate={invalidateOnMutate}
                />
            )}
            {bulkOpen && (
                <BulkReleaseDialog
                    open={bulkOpen}
                    onOpenChange={setBulkOpen}
                    entityType={entityType}
                    entityId={entityId}
                    source={source}
                    initialOwnedCount={ownedCount}
                    sourcesQueryKey={sourcesQueryKey}
                    invalidateOnMutate={invalidateOnMutate}
                />
            )}
        </>
    )
}

interface DetachSourceDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    entityType: SourcesEntityType
    entityId: string
    source: Source
    sourcesQueryKey: QueryKey
    invalidateOnMutate?: ReadonlyArray<QueryKey>
}

function DetachSourceDialog({
    open,
    onOpenChange,
    entityType,
    entityId,
    source,
    sourcesQueryKey,
    invalidateOnMutate,
}: DetachSourceDialogProps) {
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: () =>
            api.delete<DetachSourceResponse>(
                iamBase(`/${entityType}/${entityId}/sources/${source.id}`),
            ),
        onSuccess: (response) => {
            const released = response?.attributesReleasedCount ?? 0
            toast({
                description:
                    released > 0
                        ? i18n.sources.detachSuccessWithCount({count: released})
                        : i18n.sources.detachSuccess,
            })
            invalidateMutationCaches(queryClient, sourcesQueryKey, entityType, entityId, invalidateOnMutate)
            onOpenChange(false)
        },
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="detach-source-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.sources.detachTitle}</DialogTitle>
                    <DialogDescription>
                        {i18n.sources.detachBody({
                            systemName: source.targetSystemName,
                            externalId: source.externalId,
                        })}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                        data-testid="detach-source-cancel"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending}
                        data-testid="detach-source-confirm"
                    >
                        {i18n.sources.detachConfirm}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

interface BulkReleaseDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    entityType: SourcesEntityType
    entityId: string
    source: Source
    initialOwnedCount: number
    sourcesQueryKey: QueryKey
    invalidateOnMutate?: ReadonlyArray<QueryKey>
}

interface BulkReleaseResponse {
    attributesReleasedCount: number
}

function BulkReleaseDialog({
    open,
    onOpenChange,
    entityType,
    entityId,
    source,
    initialOwnedCount,
    sourcesQueryKey,
    invalidateOnMutate,
}: BulkReleaseDialogProps) {
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()

    const releaseUrl = iamBase(
        `/${entityType}/${entityId}/attributes/release-authority?targetSystemId=${source.targetSystemId}`,
    )

    const mutation = useMutation({
        mutationFn: () => api.post<BulkReleaseResponse>(releaseUrl, {}),
        onSuccess: (response) => {
            const released = response?.attributesReleasedCount ?? initialOwnedCount
            toast({
                description: i18n.sources.releaseAllSuccess({count: released}),
            })
            invalidateMutationCaches(queryClient, sourcesQueryKey, entityType, entityId, invalidateOnMutate)
            onOpenChange(false)
        },
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="bulk-release-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {i18n.sources.releaseAllTitle({systemName: source.targetSystemName})}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.sources.releaseAllBody({count: initialOwnedCount})}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                        data-testid="bulk-release-cancel"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        variant="warning"
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending}
                        data-testid="bulk-release-confirm"
                    >
                        {i18n.sources.releaseAllConfirm({count: initialOwnedCount})}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function invalidateMutationCaches(
    queryClient: ReturnType<typeof useQueryClient>,
    sourcesQueryKey: QueryKey,
    entityType: SourcesEntityType,
    entityId: string,
    invalidateOnMutate?: ReadonlyArray<QueryKey>,
): void {
    queryClient.invalidateQueries({queryKey: sourcesQueryKey})
    queryClient.invalidateQueries({queryKey: [entityType, entityId, ATTRIBUTE_LIST_QUERY_KEY]})
    queryClient.invalidateQueries({queryKey: [entityType, entityId]})
    queryClient.invalidateQueries({queryKey: [entityType]})
    for (const key of invalidateOnMutate ?? []) {
        queryClient.invalidateQueries({queryKey: key as QueryKey})
    }
}

function truncate(value: string, max: number): string {
    if (value.length <= max) return value
    return `${value.slice(0, max - 1)}…`
}

/* eslint-enable i18next/no-literal-string */
